import { inject, Injectable } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { FormSchema, Properties, Property } from '../models/form.model';

@Injectable({ providedIn: 'root' })
export class FormService {
  private fb = inject(FormBuilder);

  constructor() {}

  /**
   * Creates a FormGroup based on the given schema.
   * @param formSchema - The schema defining form properties and constraints.
   * @returns A FormGroup instance with controls and validators configured.
   */
  createForm(formSchema: FormSchema): FormGroup {
    const group = Object.entries(formSchema.properties).reduce(
      (acc, [key, property]) => {
        acc[key] = this.fb.control(property.defaultValue || '', this.getValidators(property));
        return acc;
      },
      {} as { [key: string]: AbstractControl },
    );

    return this.fb.group(group);
  }

  /**
   * Retrieves a form control by its ID.
   * @param controlId - The ID of the control to retrieve.
   * @returns The AbstractControl or null if not found.
   */
  getControl(form: FormGroup, controlId: string): AbstractControl | null {
    return form.get(controlId);
  }
  /**
   * Extracts properties from the schema into a structured format.
   * @param properties - The schema's properties.
   * @returns A list of property objects with IDs and metadata.
   */
  getProperties(properties: Properties): Array<{ id: string } & Property> {
    return Object.entries(properties).map(([id, value]) => ({ id, ...value }));
  }

  /**
   * Constructs a list of validators for a form control based on its schema.
   * @param property - The property metadata for the control.
   * @returns An array of Angular validators.
   */
  getValidators(property: Property): ValidatorFn[] {
    const validatorMap: Record<string, () => ValidatorFn> = {
      required: () => Validators.required,
      minLength: () => Validators.minLength(property.minLength!),
      maxLength: () => Validators.maxLength(property.maxLength!),
      pattern: () => Validators.pattern(property.pattern!),
    };

    return Object.entries(validatorMap)
      .filter(([key]) => property[key as keyof Property] !== undefined)
      .map(([, getValidator]) => getValidator());
  }

  /**
   * Extracts validation errors from the form.
   * @returns A structured object containing form control errors.
   */
  getFormErrors(form: FormGroup): Record<string, string[]> {
    return Object.entries(form.controls).reduce(
      (errors, [key, control]) => {
        if (control.errors) {
          errors[key] = Object.keys(control.errors);
        }
        return errors;
      },
      {} as Record<string, string[]>,
    );
  }
}
